/* lib/academy/access.js — состояние доступа ученика к курсу.
 * Один источник правды для трёх экранов, которые об истёкшем доступе раньше
 * не знали вовсе: карточка «Мои курсы», лендинг курса и плеер (ST-3).
 *
 * Признак «истёк» считаем по `expires_at` записи, а НЕ по `status`: статус
 * EXPIRED бэкенд не выставляет, а дерево курса при истёкшем доступе отдаёт
 * enrollment:null — отличить «доступ закончился» от «никогда не записывался»
 * можно только по дате из /api/academy/my/. */

import { buyCtaLabel } from './pricing.js';
import { ACADEMY_ERRORS } from './errorCodes.js';

/** Доступ выдан подпиской Restorium, и подписка кончилась → запись на ПАУЗЕ.
 *
 *  Сравнение строго `=== true`: старый payload (кэш ответа, лендинг до деплоя
 *  бэка) поля не содержит вовсе, а `undefined` в мягкой проверке ничем не
 *  отличается от `false` только до первого `null`/`'0'`. Обещать паузу по
 *  отсутствию признака нельзя: это перекроет живой доступ немым замком.
 *
 *  Имя поля — РОВНО `subscription_hold`, как в дереве курса
 *  (backend/academy/views.py) и в сериализаторах записи. */
export function isSubscriptionHold(enrollment) {
  return !!enrollment && enrollment.subscription_hold === true;
}

/** Доступ по записи истёк? Пустой `expires_at` = бессрочный доступ.
 *
 *  Заморозку исключаем ТОЙ ЖЕ строкой, что и лендинг (isLandingAccessExpired):
 *  на паузе `expires_at` не двигается (сдвиг на длительность паузы бэк делает
 *  только в момент разморозки), а `Enrollment.is_access_active` при этом True.
 *  Пауза длиннее остатка доступа — обычное дело, и без этой проверки карточка
 *  «Мои курсы» говорила «Доступ истёк», лендинг — «Открыть курс», а плеер —
 *  «Доступ заморожен»: единый источник правды разводил три экрана. */
export function isAccessExpired(enrollment) {
  if (enrollment && enrollment.frozen) return false;
  const iso = enrollment && enrollment.expires_at;
  if (!iso) return false;
  const ts = Date.parse(iso);
  return Number.isFinite(ts) && ts <= Date.now();
}

/** Дата доступа в виде 01.08.2026 ('' — если даты нет или она нечитаема). */
/* Пояс, в котором методист ставит «Доступ до» и в котором бэк хранит «конец
   дня» (TIME_ZONE проекта). Срок — это ДАТА из поля-календаря, а не момент:
   31.12 23:59:59 МСК у зрителя в Екатеринбурге (UTC+5) — уже 1 января, и без
   явного пояса карточка ученика, плеер и дровер проверки показывали бы разные
   даты в соседних окнах. */
export const ACCESS_TZ = 'Europe/Moscow';

export function fmtAccessDate(iso) {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '';
  return new Date(ts).toLocaleDateString('ru-RU',
    { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ACCESS_TZ });
}

/* Отдельной accessExpiredLabel() здесь больше нет: ту же подпись собирает
   accessState() ниже, и два независимых источника одного текста — это способ
   получить два разных текста в соседних местах экрана. */

/** Состояние срока доступа для карточки курса (ST-11): ЧЕТЫРЕ различимых
 *  состояния — активное с датой, заморозка организатора, истечение срока и
 *  пауза подписки. `null` = показывать нечего: без `expires_at` доступ
 *  пожизненный, и подпись про дату была бы выдумкой.
 *
 *  ПОРЯДОК ПРОВЕРОК: заморозка → истёкший срок → пауза подписки.
 *  Заморозка первой — так же, как в `services.lesson_lock` на бэке («заморозка
 *  перебивает все прочие коды»), иначе два экрана назовут одному ученику разные
 *  причины. Срок раньше паузы — потому что пауза, в отличие от заморозки, часы
 *  НЕ останавливает (см. развёрнутое объяснение в теле функции). */
export function accessState(enrollment) {
  if (!enrollment) return null;
  if (enrollment.frozen) {
    // «До когда» у паузы даты НЕТ: разморозку делает организатор вручную, конца
    // у заморозки в модели не существует (Enrollment.frozen_at без парного
    // «до»). Молчать об этом нельзя — рядом истёкшая карточка называет свою
    // дату, и человек читает отсутствие даты как недосказанность. Поэтому
    // говорим то, что для ученика и есть ответ: время не горит, снимает
    // организатор.
    return { kind: 'paused', icon: 'pause', text: 'Доступ на паузе',
      hint: 'Таймер доступа остановлен — срок продлится на время паузы. Снимает организатор обучения.' };
  }
  const date = fmtAccessDate(enrollment.expires_at);
  // ИСТЁКШИЙ СРОК ПЕРЕБИВАЕТ ПАУЗУ ПОДПИСКИ, и это не произвол.
  //
  // Пауза, в отличие от заморозки, часы НЕ останавливает: сдвиг срока делает
  // только `services.unfreeze_enrollment` (по `frozen_at`), а снятие паузы —
  // это `et.hold_at = None` и ничего больше. Значит у записи, которой методист
  // выставил срок вручную, доступ может честно догореть ПОКА идёт пауза.
  // Сказать такому ученику «продлите подписку, продолжите с того же места»
  // значило бы соврать дважды: подписка доступ не вернёт (`is_access_active`
  // останется False → `get_access` отдаст None), а плеер в той же сессии
  // покажет «Доступ истёк» — те самые две разные причины одного замка.
  //
  // При обычной выдаче по подписке срока нет вовсе (`expires_at` пуст), так что
  // сюда попадает только редкий случай ручного срока — и попадает верно.
  if (date && isAccessExpired(enrollment)) {
    return { kind: 'expired', icon: 'clock', text: `Доступ истёк · до ${date}` };
  }
  // Пауза подписки: срок жив, заморозки нет, а уроки заперты. Тексты — из
  // реестра кодов, те же, что печатает плеер, чтобы формулировка не разъехалась.
  if (isSubscriptionHold(enrollment)) {
    const d = ACADEMY_ERRORS.locked_subscription;
    return { kind: 'subscription', icon: 'credit-card', text: d.title, hint: d.hint };
  }
  // Нечитаемую дату молчим: «Доступ до Invalid Date» хуже отсутствия подписи.
  if (!date) return null;
  return { kind: 'active', icon: 'clock', text: `Доступ до ${date}` };
}

/** Лендинг курса: запись есть, но доступ по ней закончился — CTA обязан
 *  предлагать продление, а не «Продолжить». Источник — `can_continue` из
 *  payload лендинга (бэк считает его как «доступ активен и не на паузе»),
 *  поэтому заморозку исключаем отдельно: паузу продлевать нечего.
 *
 *  Паузу подписки исключаем ТОЙ ЖЕ строкой и по той же причине: `can_continue`
 *  на ней теперь false (кнопке некуда вести — все уроки заперты), но доступ к
 *  курсу НЕ кончился. Без этой проверки лендинг сказал бы приостановленному
 *  «Доступ истёк» и открыл продажу тарифа (CourseLanding.jsx canBuyTariff) —
 *  предложил бы купить второй раз то, что у него уже есть, вместо продления
 *  подписки Restorium. */
export function isLandingAccessExpired(enrollment) {
  return !!enrollment && enrollment.can_continue === false
    && !enrollment.frozen && !isSubscriptionHold(enrollment)
    && enrollment.status !== 'completed';
}

/** Запись из /my/ по курсу плеера (id приходит из URL — сравниваем как строки). */
export function findMyEnrollment(list, courseId) {
  if (!Array.isArray(list)) return null;
  return list.find((e) => String(e.course_id) === String(courseId)) || null;
}

/** Подпись главной CTA лендинга курса.
 *
 *  ГЛАВНОЕ ПРАВИЛО: состояние доступа ВЫИГРЫВАЕТ у статической подписи блока.
 *  Подпись `blockLabel` пишет методист в блоке лендинга, и она одна на всех
 *  посетителей страницы. Пока она перекрывала состояние, на любом лендинге с
 *  заполненным «текстом кнопки» истёкший, незаписанный и активный ученик видели
 *  одну и ту же кнопку: «Продлить доступ» не появлялось никогда, а записанному
 *  продавали уже купленный курс. Поэтому у записи (enr) подпись всегда своя;
 *  статическая остаётся для анонима и того, кто ещё не покупал.
 *
 *  AD-9: цена на кнопке — из ТАРИФОВ, если они есть (курс с тарифами по цене
 *  курса не продаётся вовсе, и печатать её значит называть сумму, которую с
 *  покупателя не спишут). */
export function courseCtaLabel({ course, enr, tariffs, blockLabel } = {}) {
  const c = course || {};
  if (enr) {
    if (enr.status === 'completed') return c.issues_certificate ? 'Открыть сертификат' : 'Пройти заново';
    if (isLandingAccessExpired(enr)) return 'Продлить доступ';
    // Заморозка и пауза подписки — «курс на месте, но заперт»: продолжать
    // нечего (следующего урока нет), а «Продлить доступ» здесь — не тот
    // продукт. Ведём в курс, где экран называет настоящую причину.
    if (enr.frozen || isSubscriptionHold(enr)) return 'Открыть курс';
    return 'Продолжить';
  }
  // Курс «только по записи методистом» самозапись НЕ принимает: self_enroll
  // отвечает invite_only. Обещать «Начать» и отказывать по нажатию — тупик,
  // в который посетитель приходит трижды (кнопка есть в hero, в тарифах и в CTA),
  // а методист об этом не узнаёт никогда. Единственное осмысленное действие тут —
  // оставить заявку, поэтому подпись обязана вести именно к ней. Заданный
  // методистом текст кнопки для invite-курса тоже не пускаем: он снова пообещает
  // запись, которой не будет.
  if (isInviteOnly(c)) return 'Оставить заявку';
  const custom = typeof blockLabel === 'string' ? blockLabel.trim() : '';
  if (custom) return custom;
  if (c.is_paid) return buyCtaLabel(c, tariffs);
  // «Начать», а не «Записаться»: для бесплатного курса запись — техническая
  // подробность, а человек нажимает кнопку, чтобы приступить к обучению.
  return 'Начать';
}

/** Курс принимает только запись методистом: самозапись и покупка закрыты гейтом
 *  бэка (`invite_only`). Платный invite-курс покупается штатно — гейт стоит на
 *  self_enroll, а не на чекауте. */
export function isInviteOnly(course) {
  const c = course || {};
  return c.access === 'invite' && !c.is_paid;
}

/** Что показывать в плеере, когда записи в дереве нет: 'player' | 'probing' |
 *  'expired' | 'redirect'.
 *
 *  Гонка, ради которой это вынесено в чистую функцию (замер на деве: редирект
 *  уходил на 153 мс, ответ /my/ приходил на 160 мс). Дерево отдаёт
 *  enrollment:null И при истёкшем доступе, и когда ученик никогда не
 *  записывался; различает их ТОЛЬКО /api/academy/my/. Прежний гард считался как
 *  «не записан И запрос грузится» и окна между рендерами не закрывал: на
 *  рендере, где `notEnrolled` впервые становится true, состояние запроса ещё
 *  принадлежит ПРЕДЫДУЩЕМУ ключу (loading=false, data=null) — «истёк» не
 *  вычислялся, и navigate уходил в том же коммите. Поэтому вход здесь не
 *  «грузится», а `myResolved` — «ответ /my/ по ЭТОМУ курсу уже получен».
 *  Пока его нет, редирект запрещён.
 *
 *  s = { hasTree, enrolled, slug, treeLoading, pendingPurchase,
 *        awaitingEnrollment, myResolved, myEnrollment } */
export function playerAccessView(s) {
  const st = s || {};
  if (!st.hasTree) return 'player';                 // дерева ещё нет — решает QueryBoundary
  if (st.enrolled) return 'player';
  // Возврат с оплаты: запись вот-вот применится колбэком шлюза — ни редиректа,
  // ни экрана «истёк» (в /my/ ещё старое состояние).
  if (st.pendingPurchase || st.awaitingEnrollment) return 'player';
  if (st.treeLoading) return 'probing';             // дерево перечитывается — не решаем
  if (!st.myResolved) return 'probing';             // ответ /my/ ещё не пришёл
  if (isAccessExpired(st.myEnrollment)) return 'expired';
  if (!st.slug) return 'player';                    // редиректить некуда — старый in-player барьер
  return 'redirect';
}
